/**
 * image-compression.js
 * Browser-side JPEG compression utility for slip screenshots.
 * Uses canvas API directly — not a Capacitor swap point.
 */

/**
 * Compress a File/Blob to JPEG base64, constraining dimensions and quality.
 * Must never throw — returns original base64 on any failure.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number, maxBase64Chars?: number }} options
 * @returns {Promise<{ base64: string, mediaType: string, byteLength: number, originalByteLength: number }>}
 */
export async function prepareSlipImageForUpload(file, options = {}) {
  const {
    maxDimension = 1800,
    quality = 0.82,
    maxBase64Chars = 3_750_000,
  } = options;

  const originalByteLength = file.size;

  // Read file to base64 as fallback
  let originalBase64 = null;
  let originalMediaType = file.type || 'image/jpeg';

  try {
    const url = URL.createObjectURL(file);

    try {
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image load failed'));
        image.src = url;
      });

      // Calculate scaled dimensions preserving aspect ratio
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Draw onto offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Export as JPEG
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');

      // If compressed result exceeds limit, still return it — the server
      // has its own size guard and will skip gracefully.
      const byteLength = Math.ceil(base64.length * 3 / 4);

      return { base64, mediaType: 'image/jpeg', byteLength, originalByteLength };
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    // Canvas failed — fall back to original file as base64
    console.error('[image-compression] Canvas compression failed, using original:', err.message);

    try {
      if (!originalBase64) {
        originalBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const parts = reader.result.split(',');
            resolve(parts[1] || '');
          };
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(file);
        });
      }

      const byteLength = Math.ceil(originalBase64.length * 3 / 4);
      return { base64: originalBase64, mediaType: originalMediaType, byteLength, originalByteLength };
    } catch (fallbackErr) {
      console.error('[image-compression] Fallback read also failed:', fallbackErr.message);
      // Absolute last resort — return empty but valid shape
      return { base64: '', mediaType: originalMediaType, byteLength: 0, originalByteLength };
    }
  }
}
